/**
 * Token refresh command handlers and startup auto-refresh support.
 */

import axios from 'axios';
import jwt from 'jsonwebtoken';
import { CLI_CONFIG } from '../config.js';
import { cliError } from '../utils/errors.js';
import { formatInfo, formatSuccess } from '../utils/format.js';
import { SessionManager } from '../utils/session.js';

const TOKEN_REFRESH_PATH = '/mcp/token/refresh';
const AUTO_REFRESH_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

interface TokenRefreshResponse {
  success: true;
  token: string;
  expiresAt: string;
}

interface AutoRefreshOptions {
  cliToken?: string;
  hasTokenOverride?: boolean;
  now?: number;
}

function refreshUrl(apiUrl: string): string {
  return `${apiUrl.replace(/\/+$/, '')}${TOKEN_REFRESH_PATH}`;
}

function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const maybe = error as { response?: { status?: unknown }; code?: unknown };
  if (typeof maybe.response?.status === 'number') return maybe.response.status;
  if (typeof maybe.code === 'number') return maybe.code;
  return undefined;
}

function getErrorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null) {
    return 'unknown error';
  }

  const maybe = error as {
    message?: unknown;
    response?: {
      status?: unknown;
      statusText?: unknown;
      data?: unknown;
    };
  };
  const status = typeof maybe.response?.status === 'number' ? maybe.response.status : undefined;
  const statusText =
    typeof maybe.response?.statusText === 'string' && maybe.response.statusText
      ? maybe.response.statusText
      : undefined;
  const data = maybe.response?.data;
  let bodyMessage: string | undefined;
  if (typeof data === 'object' && data !== null) {
    const body = data as { message?: unknown; error?: unknown };
    bodyMessage =
      typeof body.message === 'string'
        ? body.message
        : typeof body.error === 'string'
          ? body.error
          : undefined;
  }

  if (status != null) {
    return `HTTP ${status}${statusText ? ` ${statusText}` : ''}${bodyMessage ? `: ${bodyMessage}` : ''}`;
  }
  if (typeof maybe.message === 'string' && maybe.message) {
    return maybe.message;
  }
  return 'unknown error';
}

function validateRefreshResponse(data: unknown): TokenRefreshResponse {
  if (typeof data !== 'object' || data === null) {
    throw new Error('server returned malformed token refresh response');
  }

  const response = data as {
    success?: unknown;
    token?: unknown;
    expiresAt?: unknown;
  };
  if (
    response.success !== true ||
    typeof response.token !== 'string' ||
    response.token.length === 0 ||
    typeof response.expiresAt !== 'string' ||
    Number.isNaN(Date.parse(response.expiresAt))
  ) {
    throw new Error('server returned malformed token refresh response');
  }

  return {
    success: true,
    token: response.token,
    expiresAt: response.expiresAt,
  };
}

async function requestTokenRefresh(token: string, apiUrl: string): Promise<TokenRefreshResponse> {
  const response = await axios.post(
    refreshUrl(apiUrl),
    undefined,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return validateRefreshResponse(response.data);
}

export function getJwtExpiryMs(token: string): number | null {
  try {
    const decoded = jwt.decode(token);
    if (typeof decoded !== 'object' || decoded === null) {
      return null;
    }

    const exp = decoded.exp;
    if (typeof exp !== 'number') {
      return null;
    }

    return exp * 1000;
  } catch {
    return null;
  }
}

export function isTokenExpiringWithin(token: string, windowMs = AUTO_REFRESH_WINDOW_MS, now = Date.now()): boolean {
  const expiresAtMs = getJwtExpiryMs(token);
  if (expiresAtMs == null) {
    return false;
  }

  return expiresAtMs - now <= windowMs;
}

/**
 * Best-effort startup refresh for tokens that live in the saved config file.
 * CLI/env token overrides are per-invocation and must never be refreshed here.
 */
export async function autoRefreshSavedTokenIfNeeded(options: AutoRefreshOptions = {}): Promise<void> {
  if (options.hasTokenOverride || options.cliToken || process.env.HYPERTASKS_JWT_TOKEN) {
    return;
  }

  let token: string;
  let apiUrl: string;
  try {
    const config = SessionManager.loadConfig();
    if (!config.token) {
      return;
    }
    token = config.token;
    apiUrl = config.apiUrl || CLI_CONFIG.DEFAULT_API_URL;
  } catch {
    return;
  }

  if (!isTokenExpiringWithin(token, AUTO_REFRESH_WINDOW_MS, options.now ?? Date.now())) {
    return;
  }

  try {
    const refreshed = await requestTokenRefresh(token, apiUrl);
    SessionManager.saveToken(refreshed.token, apiUrl);
  } catch (error) {
    if (getErrorStatus(error) === 404) {
      return;
    }
    console.error(formatInfo('Token auto-refresh failed; continuing with existing token.'));
  }
}

export async function refreshTokenCommand(
  cliToken?: string,
  cliApiUrl?: string,
  json?: boolean,
): Promise<void> {
  const token = SessionManager.getToken(cliToken);
  if (!token) {
    cliError(
      'Authentication required. Run: hypertask login --token <jwt>\nOr set HYPERTASKS_JWT_TOKEN environment variable',
      1,
    );
  }

  const apiUrl = SessionManager.getApiUrl(cliApiUrl);
  try {
    const refreshed = await requestTokenRefresh(token, apiUrl);
    const shouldSave = !cliToken && !process.env.HYPERTASKS_JWT_TOKEN;
    if (shouldSave) {
      SessionManager.saveToken(refreshed.token, apiUrl);
    }

    const result = {
      success: true,
      token: refreshed.token,
      expiresAt: refreshed.expiresAt,
      saved: shouldSave,
    };

    if (json) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    console.log(formatSuccess('Token refreshed'));
    console.log(`Expires at: ${refreshed.expiresAt}`);
    if (shouldSave) {
      console.log('Token saved to ~/.hypertask/config.json');
    } else {
      console.log(formatInfo('Used token override; refreshed token was not saved.'));
    }
  } catch (error) {
    cliError(`Token refresh failed: ${getErrorMessage(error)}`, 1);
  }
}
